/**
 * Format a number as Korean currency (원)
 */
export function formatCurrency(value: number): string {
  return value.toLocaleString('ko-KR') + '원'
}

/**
 * Format a number with Korean unit suffixes (만, 억)
 */
export function formatKoreanUnit(value: number): string {
  if (value >= 100000000) {
    return (value / 100000000).toFixed(1) + '억'
  }
  if (value >= 10000) {
    return (value / 10000).toFixed(0) + '만'
  }
  return value.toLocaleString('ko-KR')
}

/**
 * Format a percentage
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return value.toFixed(decimals) + '%'
}

/**
 * Format a date as YYYY-MM-DD in the user's local timezone.
 *
 * Why: `toISOString()` returns UTC, which produces the previous day for KST users
 * between 00:00–08:59 KST, breaking "today" comparisons across the app.
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Today as YYYY-MM-DD in local timezone. */
export function getLocalDateString(date: Date = new Date()): string {
  return formatDate(date)
}

/**
 * Format a date as relative time (e.g., "3일 전")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return '오늘'
  if (diffDays === 1) return '어제'
  if (diffDays < 7) return `${diffDays}일 전`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}주 전`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월 전`
  return `${Math.floor(diffDays / 365)}년 전`
}
